import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';

// components
import { FormattedMessage } from 'react-intl';
import ArrowDownIcon from 'dibs-vg/dist/react/arrow-down';

// styles
import styles from './SbRespStickyMenu.scss';

const MENU_PADDING = 27;
const RESET_PADDING = 115;
const GLOBAL_NAV_HEIGHT = 99;

export class SbRespStickyMenuComponent extends React.Component {
    constructor() {
        super();
        this.state = {
            top: 0,
            menuHeight: 0,
            height: '100%',
            showCta: false,
            removeCta: false,
            menuWrapperLeft: 0,
            menuWrapperHeight: 0,
            position: 'absolute',
        };
        this.ticking = false;
        this.menuTicking = false;
        this.stickyMenu = React.createRef();
        this.stickyMenuWrapper = React.createRef();
        this.scrollToTop = this.scrollToTop.bind(this);
        this.handlePageScroll = this.handlePageScroll.bind(this);
        this.handleMenuScroll = this.handleMenuScroll.bind(this);
        this.updateMenuHeights = this.updateMenuHeights.bind(this);
    }

    componentDidMount() {
        const menuHeight = window.innerHeight - GLOBAL_NAV_HEIGHT - MENU_PADDING;
        const {
            height: menuWrapperHeight,
            top: stickyMenuWrapperTop,
            left: stickyMenuWrapperLeft,
        } = this.getRect(this.stickyMenuWrapper);

        const isMenuFixed = stickyMenuWrapperTop - GLOBAL_NAV_HEIGHT <= 0;
        const isMenuAtBottomOfPage =
            menuWrapperHeight - GLOBAL_NAV_HEIGHT - menuHeight + stickyMenuWrapperTop < 0;

        let top = 0;

        if (isMenuFixed) {
            top = GLOBAL_NAV_HEIGHT;
        } else if (isMenuAtBottomOfPage) {
            top = menuWrapperHeight - menuHeight;
        }

        this.setState({
            top,
            menuHeight,
            menuWrapperHeight,
            menuWrapperLeft: stickyMenuWrapperLeft,
            position: isMenuFixed ? 'fixed' : 'absolute',
        });

        window.addEventListener('scroll', this.handlePageScroll);
        window.addEventListener('resize', this.updateMenuHeights);
        this.stickyMenu.current.addEventListener('scroll', this.handleMenuScroll);
    }

    componentDidUpdate(prevProps, prevState) {
        const { refetchInFlight: prevRefetchInFlight } = prevProps;
        const { refetchInFlight } = this.props;
        this.updateMenuHeights();
        this.handlePageScroll();

        if (
            prevState.position !== this.state.position &&
            this.state.position === 'fixed' &&
            !this.state.showCta
        ) {
            this.setState({ showCta: true });
        }

        if (prevRefetchInFlight && !refetchInFlight) {
            this.scrollToTop();
        }
    }

    getRect(reactElem) {
        return reactElem.current.getBoundingClientRect();
    }

    scrollToTop() {
        const { top } = this.getRect(this.stickyMenuWrapper);
        window.scrollTo(window.scrollX, window.scrollY + top - RESET_PADDING);
    }

    updateMenuHeights() {
        const { menuHeight, menuWrapperHeight } = this.state;
        const newMenuHeight = window.innerHeight - GLOBAL_NAV_HEIGHT - MENU_PADDING;
        const { height: newMenuWrapperHeight } = this.getRect(this.stickyMenuWrapper);

        const shouldResetMenuHeights =
            newMenuHeight !== menuHeight || menuWrapperHeight !== newMenuWrapperHeight;

        if (shouldResetMenuHeights) {
            this.setState(
                { menuHeight: newMenuHeight, menuWrapperHeight: newMenuWrapperHeight },
                () => this.setStickyMenuPosition('RESIZE_MENU')
            );
        }
    }

    handlePageScroll() {
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                this.setStickyMenuPosition(this.getStickyMenuPositionType(), () => {
                    this.ticking = false;
                });
            });
            this.ticking = true;
        }
    }

    handleMenuScroll() {
        const { removeCta } = this.state;
        if (!this.menuTicking && !removeCta) {
            window.requestAnimationFrame(() => {
                this.setState({ removeCta: true });
                this.menuTicking = false;
            });
            this.menuTicking = true;
        }
    }

    getStickyMenuPositionType() {
        const { position, menuHeight, menuWrapperHeight, top, menuWrapperLeft } = this.state;
        const { top: stickyMenuWrapperTop, left: stickyMenuWrapperLeft } = this.getRect(
            this.stickyMenuWrapper
        );
        const scrollPosition = window.scrollY;

        const isAtTop = scrollPosition === 0;
        const isTransitionFromScrollToFixedTop =
            top === 0 && position === 'absolute' && stickyMenuWrapperTop - GLOBAL_NAV_HEIGHT < 0;
        const isTransitionFromFixedToScrollTop =
            position === 'fixed' && stickyMenuWrapperTop - GLOBAL_NAV_HEIGHT > 0;
        const isTransitionFromFixedToScrollBottom =
            position === 'fixed' &&
            menuWrapperHeight -
                (GLOBAL_NAV_HEIGHT + MENU_PADDING) -
                menuHeight +
                stickyMenuWrapperTop <
                0;
        const isTransitionFromScrollToFixedBottom =
            top > 0 &&
            position === 'absolute' &&
            menuWrapperHeight -
                (GLOBAL_NAV_HEIGHT + MENU_PADDING) -
                menuHeight +
                stickyMenuWrapperTop >
                0;
        const hasScrollX = position !== 'absolute' && stickyMenuWrapperLeft !== menuWrapperLeft;

        if (isAtTop) {
            return 'TOP_OF_PAGE';
        } else if (isTransitionFromScrollToFixedTop) {
            return 'FIXED';
        } else if (isTransitionFromScrollToFixedBottom) {
            return 'FIXED';
        } else if (isTransitionFromFixedToScrollTop) {
            return 'SCROLL_TOP';
        } else if (isTransitionFromFixedToScrollBottom) {
            return 'SCROLL_BOTTOM';
        } else if (hasScrollX) {
            return 'SCROLL_X';
        } else {
            return 'NO_CHANGE';
        }
    }

    setStickyMenuPosition(type, callback = () => {}) {
        const { menuWrapperHeight, menuHeight } = this.state;
        const left =
            type === 'FIXED' || type === 'SCROLL_X' ? this.getRect(this.stickyMenuWrapper).left : 0;
        let state;

        switch (type) {
            case 'TOP_OF_PAGE':
                state = { position: 'absolute', top: 0, left, height: '100%' };
                break;
            case 'FIXED':
                state = {
                    left,
                    position: 'fixed',
                    height: menuHeight,
                    top: GLOBAL_NAV_HEIGHT,
                };
                break;
            case 'SCROLL_TOP':
                state = { position: 'absolute', top: 0, left, height: menuHeight };
                break;
            case 'SCROLL_BOTTOM':
                state = {
                    left,
                    height: menuHeight,
                    position: 'absolute',
                    top: menuWrapperHeight - (menuHeight + MENU_PADDING),
                };
                break;
            case 'SCROLL_X':
                state = { left, menuWrapperLeft: left };
                break;
            case 'RESIZE_MENU':
                state = { height: menuHeight };
            default:
                break;
        }

        if (state) {
            this.setState(state, () => callback());
        } else {
            callback();
        }
    }

    render() {
        const { children } = this.props;
        const { position, top, left, height, showCta, removeCta } = this.state;
        const ctaClassNames = classnames(styles.ctaContent, styles.fadeIn, {
            [styles.fadeOut]: removeCta,
        });

        return (
            <div ref={this.stickyMenuWrapper} className={styles.wrapper}>
                <div
                    ref={this.stickyMenu}
                    className={styles.menu}
                    style={{ top, position, left, height }}
                >
                    {children}
                </div>
                {showCta && (
                    <div className={styles.ctaWrapper} style={{ position }}>
                        <div className={ctaClassNames}>
                            <div className={styles.arrow}>
                                <ArrowDownIcon />
                            </div>
                            <span className={styles.ctaText}>
                                <FormattedMessage
                                    id="SbRespStickyMenu.ctaText"
                                    defaultMessage="Scroll to see more filters"
                                />
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

SbRespStickyMenuComponent.propTypes = {
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    refetchInFlight: PropTypes.bool,
};

const mapStateToProps = state => {
    const { filters } = state;

    return {
        refetchInFlight: filters.refetchInFlight,
    };
};

export const SbRespStickyMenu = connect(mapStateToProps)(SbRespStickyMenuComponent);
